import { inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import { canCheckDeposit, Deposit, Deposits, isDepositExpired } from './deposits';
import { AnalyticsService } from '../analytics/analytics.service';
import { interval, NEVER, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ApiResponseDepositStatus, JitApi } from '../../api/jit-api';
import { HttpService } from '../http/http.service';
import { ApiResponseFindMatch, WagerApi } from '../../api/wager-api';
import { JobService } from '../job/job.service';
import { Api } from '../../api/api';
import { TournamentService } from '../tournament/tournament.service';

@Injectable({
  providedIn: 'root',
})
export class DepositCheckerService implements OnDestroy {
  private analyticsService = inject(AnalyticsService);
  private httpService = inject(HttpService);
  private jobService = inject(JobService);
  private tournamentService = inject(TournamentService);

  private deposits: Deposits | undefined;
  private lastConsumedDepositSignal: WritableSignal<Deposit | undefined> = signal(undefined);
  private startStop$ = new Subject<boolean>();
  private destroy$ = new Subject<void>();

  readonly consumedDeposit$ = this.lastConsumedDepositSignal.asReadonly();

  async initialise(_operatorId: string, _operatorPlatform: string | undefined): Promise<void> {
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));

    this.startStop$
      .pipe(
        startWith(true),
        switchMap((running) => (running ? interval(2000).pipe(startWith(0)) : NEVER)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.checkDeposits();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setDeposits(deposits: Deposits) {
    this.stop();
    this.deposits = deposits;
    this.start();
  }

  clearDeposits() {
    this.stop();
    this.deposits = undefined;
  }

  start() {
    if (!this.deposits || this.deposits.getDeposits().length === 0) {
      return;
    }

    this.startStop$.next(true);
  }

  stop() {
    this.startStop$.next(false);
  }

  private async checkDeposits() {
    if (!this.deposits || this.deposits.getDeposits().length === 0) {
      this.stop();
      return;
    }

    const deposits = this.deposits.getDeposits();

    for (const deposit of deposits) {
      try {
        await this.processDeposit(deposit);
      } catch (error) {
        await this.analyticsService.handleError(error, `Process deposit: id: ${deposit.id}`);
      }
    }
  }

  private async processDeposit(deposit: Deposit) {
    if (!this.deposits) {
      return this.logWarning('Deposits not set');
    }

    if (!canCheckDeposit(deposit)) {
      return;
    }

    const job = this.jobService.create<ApiResponseDepositStatus>(async () => {
      return await JitApi.request(this.httpService).depositStatus(deposit.id);
    });

    const depositStatus = await job.runNoThrow();

    if (depositStatus && depositStatus.data.chargeStatus === 'PAID') {
      if (depositStatus.data.depositStatus === 'AVAILABLE') {
        try {
          const job = deposit.tournamentId
            ? this.tournamentService.findTournamentMatch(
                deposit.gameId,
                deposit.tierId,
                deposit.id,
                deposit.tournamentId,
              )
            : this.jobService.create<ApiResponseFindMatch>(async () => {
                return await WagerApi.request(this.httpService).findMatch(
                  deposit.gameId,
                  deposit.tierId,
                  deposit.id,
                );
              });

          const match = await job.run();
          if (match) {
            await this.stopChecking(deposit);
            deposit.roomId = match.data.roomId;
            this.lastConsumedDepositSignal.set(deposit);
          }
        } catch (error) {
          if (Api.isHttpErrorResponse(error)) {
            if (error.status === 404 || error.status === 500) {
              await this.stopChecking(deposit);
            }
          }
        }
      } else if (depositStatus.data.depositStatus === 'AUTOREFUND') {
        await this.stopChecking(deposit);
      }
    } else if (isDepositExpired(deposit)) {
      await this.stopChecking(deposit);
    } else {
      this.deposits?.checked(deposit);
    }
  }

  private onVisibilityChange() {
    this.stop(); // Ensure clean state before restarting

    if (!document.hidden) {
      // App is back in focus
      this.start(); // Restart deposit checking immediately
    }
  }

  private async stopChecking(deposit: Deposit) {
    if (!this.deposits) {
      return;
    }
    await this.deposits.remove(deposit);
    if (this.deposits.getDeposits().length === 0) {
      this.stop();
    }
  }

  private async logWarning(message: string) {
    await this.analyticsService.errorWarning(`DepositChecker: ${message}`);
  }
}
